import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faComments,
  faPaperPlane,
  faTimes,
  faHeadset,
  faSpinner,
} from "@fortawesome/free-solid-svg-icons";
import { useLanguage } from "../../i18n/LanguageContext";
import { handleSessionExpiry } from "../../utils/session";
import "./support-chat.css";

function buildTranscript(messages) {
  return messages
    .filter((m) => m.role === "user" || m.role === "assistant")
    .map((m) => `${m.role === "user" ? "User" : "Assistant"}: ${m.text}`)
    .join("\n")
    .slice(0, 9000);
}

async function parseApiResponse(res, fallbackMessage) {
  const raw = await res.text();
  const contentType = (res.headers.get("content-type") || "").toLowerCase();

  if (contentType.includes("application/json")) {
    try {
      return JSON.parse(raw || "{}");
    } catch {
      throw new Error(fallbackMessage);
    }
  }

  if (!res.ok) {
    throw new Error(fallbackMessage);
  }

  // Accept plain-text successful responses if any appear in future.
  return { message: raw };
}

export default function SupportChatWidget() {
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const tr = (key, fallback) => {
    const value = t(key);
    return value === key ? fallback : value;
  };

  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [creatingTicket, setCreatingTicket] = useState(false);
  const [needsEscalation, setNeedsEscalation] = useState(false);
  const [suggestedCategory, setSuggestedCategory] = useState("general");
  const [messages, setMessages] = useState(() => ([
    { role: "assistant", text: tr("supportChat.welcome", "Hi! I am GharBhada Support AI. Ask me anything, and I can create a support ticket for you if needed.") },
  ]));

  const sendMessage = async () => {
    const token = localStorage.getItem("token");
    const text = input.trim();

    if (!text || sending) return;

    if (!token) {
      setMessages((prev) => [
        ...prev,
        { role: "assistant", text: tr("supportChat.loginRequired", "Please sign in first so I can securely help with your account.") },
      ]);
      navigate("/login");
      return;
    }

    const updatedMessages = [...messages, { role: "user", text }];
    setMessages(updatedMessages);
    setInput("");
    setSending(true);

    try {
      const history = updatedMessages.slice(-10).map((m) => ({
        role: m.role === "assistant" ? "assistant" : "user",
        content: m.text,
      }));

      const res = await fetch("/api/support/chat", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          message: text,
          history,
          language,
        }),
      });

      if (res.status === 401 || res.status === 403) {
        handleSessionExpiry(navigate);
        return;
      }

      const data = await parseApiResponse(res, tr("supportChat.unavailable", "Support assistant is temporarily unavailable."));
      if (!res.ok) {
        throw new Error(data.message || "Support chat failed.");
      }

      setMessages((prev) => [...prev, { role: "assistant", text: data.reply || tr("supportChat.unavailable", "Support assistant is temporarily unavailable.") }]);
      setNeedsEscalation(Boolean(data.needsHuman));
      if (data.intentCategory) {
        setSuggestedCategory(data.intentCategory);
      }
    } catch (error) {
      setMessages((prev) => [
        ...prev,
        { role: "assistant", text: error.message || tr("supportChat.unavailable", "Support assistant is temporarily unavailable.") },
      ]);
    } finally {
      setSending(false);
    }
  };

  const createTicket = async () => {
    const token = localStorage.getItem("token");
    if (!token || creatingTicket) return;

    const userFirstMessage = messages.find((m) => m.role === "user")?.text || "Support request";
    const subject = userFirstMessage.length > 120 ? `${userFirstMessage.slice(0, 117)}...` : userFirstMessage;

    setCreatingTicket(true);
    try {
      const res = await fetch("/api/support/tickets", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          subject,
          message: buildTranscript(messages),
          category: suggestedCategory || "general",
          priority: "normal",
          language,
        }),
      });

      if (res.status === 401 || res.status === 403) {
        handleSessionExpiry(navigate);
        return;
      }

      const data = await parseApiResponse(res, tr("supportChat.ticketFailed", "Unable to create a support ticket right now."));
      if (!res.ok) {
        throw new Error(data.message || "Unable to create ticket.");
      }

      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          text: `${tr("supportChat.ticketCreated", "Support ticket created successfully.")} #${data.ticketId || ""}`.trim(),
        },
      ]);
      setNeedsEscalation(false);
    } catch (error) {
      setMessages((prev) => [
        ...prev,
        { role: "assistant", text: error.message || tr("supportChat.ticketFailed", "Unable to create a support ticket right now.") },
      ]);
    } finally {
      setCreatingTicket(false);
    }
  };

  return (
    <div className="gb-support-chat">
      {open && (
        <div className="gb-support-panel" role="dialog" aria-label={tr("supportChat.title", "Support Chat")}>
          <div className="gb-support-header">
            <div>
              <h5>{tr("supportChat.title", "Support Chat")}</h5>
              <p>{tr("supportChat.subtitle", "AI-first help with human escalation")}</p>
            </div>
            <button className="gb-support-icon-btn" onClick={() => setOpen(false)} aria-label={tr("supportChat.minimize", "Minimize chat")}>
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>

          <div className="gb-support-body">
            {messages.map((message, idx) => (
              <div key={`${message.role}-${idx}`} className={`gb-support-msg ${message.role}`}>
                {message.text}
              </div>
            ))}
            {sending && (
              <div className="gb-support-msg assistant pending">
                <FontAwesomeIcon icon={faSpinner} spin /> {tr("supportChat.typing", "Thinking...")}
              </div>
            )}
          </div>

          {needsEscalation && (
            <div className="gb-support-actions">
              <button className="btn btn-outline-primary btn-sm" onClick={createTicket} disabled={creatingTicket}>
                {creatingTicket ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} spin />
                    <span>{tr("supportChat.creatingTicket", "Creating ticket...")}</span>
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faHeadset} />
                    <span>{tr("supportChat.createTicket", "Create Support Ticket")}</span>
                  </>
                )}
              </button>
            </div>
          )}

          <div className="gb-support-input-row">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") sendMessage();
              }}
              placeholder={tr("supportChat.placeholder", "Type your issue here...")}
              maxLength={2000}
            />
            <button className="btn btn-primary btn-sm" onClick={sendMessage} disabled={sending || !input.trim()}>
              <FontAwesomeIcon icon={faPaperPlane} />
            </button>
          </div>
        </div>
      )}

      {!open && (
        <button className="gb-support-launcher" onClick={() => setOpen(true)} aria-label={tr("supportChat.launcher", "Open support chat")}>
          <FontAwesomeIcon icon={faComments} />
          <span>{tr("supportChat.launcher", "Support")}</span>
        </button>
      )}
    </div>
  );
}
